import React, { useEffect, useMemo, useState } from "react";
import { useParams, Link, useNavigate } from "react-router-dom";
import axios from "axios";
import { toast } from "sonner";
import {
    Select,
    SelectContent,
    SelectItem,
    SelectTrigger,
    SelectValue,
} from "../components/ui/select";
import { Textarea } from "../components/ui/textarea";
import {
    ADULT_SIZES,
    ADULT_BASE_PRICE,
    KIDS_SIZES,
    KIDS_BASE_PRICE,
    findCategory,
    findSub,
    getProductType,
    PRODUCT_TYPES,
    ORDER_STORAGE_KEY,
    COLLECTIONS,
} from "../data/catalog";
import { useCart, fmtPrice } from "../contexts/CartContext";
import { useDefaultPrices, resolveDefaultPrice } from "../lib/defaultPrices";
import { BoltIcon } from "../components/Bolts";
import { ArrowLeft, ShoppingBag, Ruler, Wand2, ImageOff } from "lucide-react";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const ProductDetailPage = () => {
    const { category, sub, listingSlug } = useParams();
    const navigate = useNavigate();
    const { addItem } = useCart();

    const cat = findCategory(category);
    const subItem = findSub(category, sub);
    const productType = getProductType(category, sub);
    const priceMap = useDefaultPrices();
    const defaultPrice = resolveDefaultPrice(priceMap, category, sub);

    const [listing, setListing] = useState(null);
    const [loading, setLoading] = useState(true);
    const [activeImage, setActiveImage] = useState(0);

    // shirt
    const [adultSize, setAdultSize] = useState("");
    const [kidsSize, setKidsSize] = useState("");
    // apparel-single (hoodie, sweatshirt, sweatpants)
    const [appSize, setAppSize] = useState("");
    // custom engraving form
    const [customMaterial, setCustomMaterial] = useState("");
    const [customText, setCustomText] = useState("");

    useEffect(() => {
        let cancelled = false;
        (async () => {
            setLoading(true);
            try {
                const { data } = await axios.get(`${API}/listings/public/${listingSlug}`, {
                    params: { category, subcategory: sub },
                });
                if (!cancelled) setListing(data);
            } catch {
                if (!cancelled) setListing(null);
            } finally {
                if (!cancelled) setLoading(false);
            }
        })();
        return () => {
            cancelled = true;
        };
    }, [listingSlug, category, sub]);

    if (loading) {
        return <div className="container-x py-32 text-center text-storm-mist">Loading…</div>;
    }
    if (!listing) {
        return (
            <div className="container-x py-32 text-center">
                <p className="text-storm-mist mb-6">Listing not found.</p>
                <Link to={`/${category}/${sub}`} className="btn-secondary">
                    Back to {subItem?.name || "category"}
                </Link>
            </div>
        );
    }

    const title = listing.title || "Custom Item";
    const price = listing.price_cents || defaultPrice || null;

    const computeShirtPrice = () => {
        let p = 0;
        if (adultSize) p += (ADULT_BASE_PRICE + (ADULT_SIZES.find((s) => s.code === adultSize)?.upcharge || 0)) * 100;
        if (kidsSize) p += KIDS_BASE_PRICE * 100;
        return p;
    };
    const computeAppPrice = () => (appSize && price ? price : 0);

    const addToCart = () => {
        const imageUrl = listing.image_ids?.[0] ? `${API}/photos/${listing.image_ids[0]}/image` : null;
        if (productType === PRODUCT_TYPES.SHIRT) {
            if (!adultSize && !kidsSize) {
                toast.error("Pick an adult or kids size first.");
                return;
            }
            if (adultSize) {
                addItem({
                    id: `${listing.listing_slug}-adult-${adultSize}`,
                    listing_slug: listing.listing_slug,
                    title: `${title} — Adult ${adultSize}`,
                    priceCents: (ADULT_BASE_PRICE + (ADULT_SIZES.find((s) => s.code === adultSize)?.upcharge || 0)) * 100,
                    adultSize,
                    imageUrl,
                });
            }
            if (kidsSize) {
                addItem({
                    id: `${listing.listing_slug}-kids-${kidsSize}`,
                    listing_slug: listing.listing_slug,
                    title: `${title} — Kids ${kidsSize}`,
                    priceCents: KIDS_BASE_PRICE * 100,
                    kidsSize,
                    imageUrl,
                });
            }
            toast.success("Added to cart.");
        } else if (productType === PRODUCT_TYPES.APPAREL_SINGLE) {
            if (!appSize) { toast.error("Pick a size."); return; }
            if (!price) { toast.error("Price not set yet."); return; }
            addItem({ id: listing.listing_slug, listing_slug: listing.listing_slug, title: `${title} — ${appSize}`, priceCents: price, variant: appSize, imageUrl });
            toast.success("Added to cart.");
        } else {
            if (!price) {
                toast.error("Price not set. Use the quote button instead.");
                return;
            }
            addItem({ id: listing.listing_slug, listing_slug: listing.listing_slug, title, priceCents: price, imageUrl });
            toast.success("Added to cart.");
        }
    };

    const askForQuote = (subject) => {
        const lines = [
            `QUOTE REQUEST: ${title}`,
            subject ? `Topic: ${subject}` : null,
            customMaterial ? `Material/Item: ${customMaterial}` : null,
            customText ? `Engrave text / design notes:\n${customText}` : null,
            "",
            `Listing: /${category}/${sub}/${listing.listing_slug}`,
        ].filter(Boolean);
        try {
            sessionStorage.setItem(
                ORDER_STORAGE_KEY,
                JSON.stringify({ title: `Quote · ${title}`, message: lines.join("\n") })
            );
        } catch {}
        navigate("/#contact-us-now");
        setTimeout(() => {
            window.dispatchEvent(new Event("dsc:order-placed"));
            document.getElementById("contact-us-now")?.scrollIntoView({ behavior: "smooth", block: "start" });
        }, 80);
    };

    const images = listing.image_ids || [];

    return (
        <div data-testid="product-detail-page" className="container-x py-12 md:py-16">
            <Link
                to={`/${category}/${sub}`}
                data-testid="back-to-sub"
                className="inline-flex items-center gap-2 text-xs uppercase tracking-[0.25em] text-storm-bolt mb-8 hover:gap-3 transition-all"
            >
                <ArrowLeft className="w-4 h-4" /> Back to {subItem?.name}
            </Link>

            <div className="grid lg:grid-cols-2 gap-10 lg:gap-16">
                {/* Image gallery */}
                <div className="space-y-3">
                    <div className="aspect-square bg-storm-navy border border-white/10 overflow-hidden relative">
                        {images[activeImage] ? (
                            <img
                                src={`${API}/photos/${images[activeImage]}/image`}
                                alt={title}
                                className="w-full h-full object-cover"
                            />
                        ) : (
                            <div className="absolute inset-0 flex items-center justify-center text-storm-mist/30">
                                <ImageOff className="w-12 h-12" />
                            </div>
                        )}
                    </div>
                    {images.length > 1 && (
                        <div className="grid grid-cols-5 gap-2">
                            {images.map((id, i) => (
                                <button
                                    key={id}
                                    onClick={() => setActiveImage(i)}
                                    data-testid={`thumb-${i}`}
                                    className={`aspect-square overflow-hidden border ${
                                        i === activeImage ? "border-storm-bolt" : "border-white/10 hover:border-white/30"
                                    } transition-colors`}
                                >
                                    <img
                                        src={`${API}/photos/${id}/image`}
                                        alt={`view ${i + 1}`}
                                        className="w-full h-full object-cover"
                                    />
                                </button>
                            ))}
                        </div>
                    )}
                </div>

                {/* Info */}
                <div className="space-y-6">
                    <div>
                        <p className="eyebrow mb-3 flex items-center gap-2">
                            <BoltIcon className="w-3.5 h-3.5" /> {cat?.name} · {subItem?.name}
                        </p>
                        <h1 className="text-4xl md:text-5xl lg:text-6xl text-white leading-[0.95]">{title}</h1>
                        {listing.collection && (
                            <span className="inline-block mt-3 px-3 py-1 border border-storm-bolt/40 text-storm-bolt text-[11px] uppercase tracking-[0.25em] font-heading">
                                {COLLECTIONS.find((c) => c.slug === listing.collection)?.name || listing.collection}
                            </span>
                        )}
                    </div>
                    <p className="text-storm-mist text-base md:text-lg leading-relaxed">
                        {listing.description || "Premium custom build, made to order. Hit add-to-cart and we'll confirm details by email."}
                    </p>

                    {/* Price summary */}
                    {productType !== PRODUCT_TYPES.SHIRT && (
                        <div className="border-t border-white/10 pt-4">
                            <span className="eyebrow block mb-1">Price</span>
                            <span className="font-heading text-4xl text-white">
                                {price ? fmtPrice(price) : "Quote on request"}
                            </span>
                        </div>
                    )}

                    {/* SHIRT: adult + kids dropdowns */}
                    {productType === PRODUCT_TYPES.SHIRT && (
                        <ShirtPicker
                            adultSize={adultSize}
                            kidsSize={kidsSize}
                            setAdultSize={setAdultSize}
                            setKidsSize={setKidsSize}
                        />
                    )}

                    {/* APPAREL-SINGLE: hoodies/sweatshirts/sweatpants — single size dropdown */}
                    {productType === PRODUCT_TYPES.APPAREL_SINGLE && (
                        <div>
                            <label className="text-[11px] uppercase tracking-[0.2em] text-storm-mist block mb-2">
                                Size
                            </label>
                            <Select value={appSize} onValueChange={setAppSize}>
                                <SelectTrigger className="bg-storm-void border-white/10 text-white rounded-none h-12">
                                    <SelectValue placeholder="Pick a size" />
                                </SelectTrigger>
                                <SelectContent className="bg-storm-navy border-white/10 text-white rounded-none">
                                    {ADULT_SIZES.map((s) => (
                                        <SelectItem
                                            key={s.code}
                                            value={s.code}
                                            className="focus:bg-storm-bolt focus:text-black rounded-none font-heading uppercase tracking-wider text-sm"
                                        >
                                            {s.label}
                                        </SelectItem>
                                    ))}
                                </SelectContent>
                            </Select>
                        </div>
                    )}

                    {/* Add to cart row */}
                    <div className="flex flex-wrap gap-3">
                        <button
                            onClick={addToCart}
                            disabled={
                                (productType !== PRODUCT_TYPES.SHIRT && !price) ||
                                productType === PRODUCT_TYPES.LASER_ENGRAVING && !price
                            }
                            data-testid="add-to-cart"
                            className="btn-primary disabled:opacity-60 disabled:cursor-not-allowed"
                        >
                            <ShoppingBag className="w-4 h-4" /> Add to Cart
                        </button>

                        {/* Woodworking: different size? */}
                        {productType === PRODUCT_TYPES.WOODWORKING && (
                            <button
                                onClick={() => askForQuote("Different size")}
                                data-testid="quote-different-size"
                                className="btn-secondary"
                            >
                                <Ruler className="w-4 h-4" /> Need a different size? Click here to ask for a quote!
                            </button>
                        )}

                        {/* Laser engraving: custom quote */}
                        {productType === PRODUCT_TYPES.LASER_ENGRAVING && (
                            <button
                                onClick={() => askForQuote("Custom engraving request")}
                                data-testid="quote-custom-engraving"
                                className="btn-secondary"
                            >
                                <Wand2 className="w-4 h-4" /> Request Custom Engraving
                            </button>
                        )}
                    </div>

                    {/* Laser engraving custom form (always visible on misc-laser) */}
                    {productType === PRODUCT_TYPES.LASER_ENGRAVING && (
                        <div className="surface-solid p-6 mt-4">
                            <h3 className="font-heading text-xl uppercase tracking-wider text-white mb-2">
                                Or — get anything engraved
                            </h3>
                            <p className="text-storm-mist text-sm leading-relaxed mb-5">
                                Tell us what you'd like engraved and on what. We'll quote and send a payment link.
                            </p>
                            <div className="space-y-3">
                                <div>
                                    <label className="text-[11px] uppercase tracking-[0.2em] text-storm-mist block mb-1.5">
                                        Material / Item
                                    </label>
                                    <input
                                        type="text"
                                        value={customMaterial}
                                        onChange={(e) => setCustomMaterial(e.target.value)}
                                        data-testid="custom-material"
                                        placeholder="e.g. walnut cutting board, glass cup, leather wallet"
                                        className="w-full bg-storm-void border border-white/10 text-white px-3 h-11 focus:border-storm-bolt focus:outline-none text-sm"
                                    />
                                </div>
                                <div>
                                    <label className="text-[11px] uppercase tracking-[0.2em] text-storm-mist block mb-1.5">
                                        Design / Text
                                    </label>
                                    <Textarea
                                        value={customText}
                                        onChange={(e) => setCustomText(e.target.value)}
                                        rows={4}
                                        data-testid="custom-text"
                                        placeholder="Describe what you want engraved — text, logo, dimensions, font..."
                                        className="bg-storm-void border-white/10 text-white rounded-none focus-visible:ring-storm-bolt focus-visible:ring-offset-0 focus-visible:border-storm-bolt resize-none"
                                    />
                                </div>
                                <button onClick={() => askForQuote("Custom engraving request")} className="btn-primary w-full justify-center">
                                    Send Engraving Request
                                </button>
                            </div>
                        </div>
                    )}
                </div>
            </div>
        </div>
    );
};

const ShirtPicker = ({ adultSize, kidsSize, setAdultSize, setKidsSize }) => {
    const adultPrice = adultSize
        ? ADULT_BASE_PRICE + (ADULT_SIZES.find((s) => s.code === adultSize)?.upcharge || 0)
        : 0;
    const kidsPrice = kidsSize ? KIDS_BASE_PRICE : 0;
    const total = adultPrice + kidsPrice;

    return (
        <div className="border-t border-white/10 pt-5 space-y-4">
            <div className="grid grid-cols-2 gap-4">
                <div>
                    <label className="text-[11px] uppercase tracking-[0.2em] text-storm-mist block mb-2">
                        Adult — ${ADULT_BASE_PRICE} base
                    </label>
                    <Select value={adultSize} onValueChange={setAdultSize}>
                        <SelectTrigger className="bg-storm-void border-white/10 text-white rounded-none h-12">
                            <SelectValue placeholder="Adult size" />
                        </SelectTrigger>
                        <SelectContent className="bg-storm-navy border-white/10 text-white rounded-none">
                            {ADULT_SIZES.map((s) => (
                                <SelectItem
                                    key={s.code}
                                    value={s.code}
                                    className="focus:bg-storm-bolt focus:text-black rounded-none font-heading uppercase tracking-wider text-sm"
                                >
                                    {s.label}
                                </SelectItem>
                            ))}
                        </SelectContent>
                    </Select>
                </div>
                <div>
                    <label className="text-[11px] uppercase tracking-[0.2em] text-storm-mist block mb-2">
                        Kids — ${KIDS_BASE_PRICE} flat
                    </label>
                    <Select value={kidsSize} onValueChange={setKidsSize}>
                        <SelectTrigger className="bg-storm-void border-white/10 text-white rounded-none h-12">
                            <SelectValue placeholder="Kids size" />
                        </SelectTrigger>
                        <SelectContent className="bg-storm-navy border-white/10 text-white rounded-none">
                            {KIDS_SIZES.map((s) => (
                                <SelectItem
                                    key={s.code}
                                    value={s.code}
                                    className="focus:bg-storm-bolt focus:text-black rounded-none font-heading uppercase tracking-wider text-sm"
                                >
                                    {s.label}
                                </SelectItem>
                            ))}
                        </SelectContent>
                    </Select>
                </div>
            </div>
            {(adultSize || kidsSize) && (
                <div className="text-sm space-y-1 border-t border-white/5 pt-3">
                    {adultSize && (
                        <div className="flex items-center justify-between text-storm-mist">
                            <span>Adult {adultSize}</span>
                            <span className="text-white">${adultPrice}</span>
                        </div>
                    )}
                    {kidsSize && (
                        <div className="flex items-center justify-between text-storm-mist">
                            <span>Kids {kidsSize}</span>
                            <span className="text-white">${kidsPrice}</span>
                        </div>
                    )}
                    <div className="flex items-center justify-between pt-2 mt-2 border-t border-white/5">
                        <span className="eyebrow">Total</span>
                        <span className="font-heading text-2xl text-white">${total}</span>
                    </div>
                </div>
            )}
        </div>
    );
};

export default ProductDetailPage;
